import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import FadeIn from '../shared/FadeIn';
import { useT } from '../../i18n/LanguageContext';
import strings from './ContactPage.i18n';
import './ContactPage.css';

// Fix default marker icon (Leaflet + webpack issue)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const ContactPage = () => {
  const t = useT(strings);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    const msg = `${t.waPrefix} ${firstName} ${lastName} ${t.waFrom} ${company}. ${message}`;
    window.open(`https://wa.me/601163688943?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <div className="contact-page">
      {/* Section 1: Contact Hero */}
      <section className="contact-hero gradient-hero">
        <FadeIn>
          <span className="contact-hero__label">{t.heroLabel}</span>
        </FadeIn>
        <FadeIn delay={100}>
          <h1 className="contact-hero__title">
            {t.heroTitlePrefix}<span className="text-accent">{t.heroTitleAccent}</span>
          </h1>
        </FadeIn>
        <FadeIn delay={200}>
          <p className="contact-hero__subtitle">
            {t.heroSubtitle}
          </p>
        </FadeIn>
      </section>

      {/* Section 2: Two-column layout */}
      <section className="contact-content">
        {/* Left Column: Contact Form */}
        <FadeIn>
          <div className="contact-form-card">
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="contact-form__row">
                <div className="contact-form__group">
                  <label className="contact-form__label" htmlFor="firstName">
                    {t.firstName}
                  </label>
                  <input
                    className="contact-form__input"
                    type="text"
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                </div>
                <div className="contact-form__group">
                  <label className="contact-form__label" htmlFor="lastName">
                    {t.lastName}
                  </label>
                  <input
                    className="contact-form__input"
                    type="text"
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="contact-form__group">
                <label className="contact-form__label" htmlFor="email">
                  {t.workEmail}
                </label>
                <input
                  className="contact-form__input"
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="contact-form__group">
                <label className="contact-form__label" htmlFor="company">
                  {t.company}
                </label>
                <input
                  className="contact-form__input"
                  type="text"
                  id="company"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  required
                />
              </div>

              <div className="contact-form__group">
                <label className="contact-form__label" htmlFor="teamSize">
                  {t.teamSize}
                </label>
                <select
                  className="contact-form__select"
                  id="teamSize"
                  value={teamSize}
                  onChange={(e) => setTeamSize(e.target.value)}
                  required
                >
                  <option value="" disabled>{t.selectTeamSize}</option>
                  <option value="1-5">1-5</option>
                  <option value="6-20">6-20</option>
                  <option value="21-50">21-50</option>
                  <option value="51-200">51-200</option>
                  <option value="200+">200+</option>
                </select>
              </div>

              <div className="contact-form__group">
                <label className="contact-form__label" htmlFor="message">
                  {t.message}
                </label>
                <textarea
                  className="contact-form__textarea"
                  id="message"
                  rows="4"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </div>

              <button className="contact-form__submit" type="submit">
                {t.sendMessage}
              </button>
            </form>
          </div>
        </FadeIn>

        {/* Right Column: Info Cards + Map */}
        <div className="contact-info">
          <FadeIn delay={100}>
            <div className="contact-info-card">
              <h4 className="contact-info-card__title">
                <span>&#128231;</span> {t.emailUs}
              </h4>
              <p className="contact-info-card__text">hello@sapot.ai</p>
              <p className="contact-info-card__text">support@sapot.ai</p>
            </div>
          </FadeIn>

          <FadeIn delay={200}>
            <div className="contact-info-card">
              <h4 className="contact-info-card__title">
                <span>&#128222;</span> {t.callUs}
              </h4>
              <p className="contact-info-card__text">+60 11-6368 8943</p>
            </div>
          </FadeIn>

          <FadeIn delay={300}>
            <div className="contact-info-card">
              <h4 className="contact-info-card__title">
                <span>&#128205;</span> {t.visitUs}
              </h4>
              <p className="contact-info-card__text">63-4B, Tingkat 4,</p>
              <p className="contact-info-card__text">Jalan Anggerik Vanilla T 31/T,</p>
              <p className="contact-info-card__text">Kota Kemuning, 40460 Shah Alam, Selangor</p>
            </div>
          </FadeIn>

          <FadeIn delay={400}>
            <div className="contact-map-wrapper">
              <MapContainer
                center={[3.0068, 101.5367]}
                zoom={15}
                scrollWheelZoom={false}
                className="contact-map"
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker position={[3.0068, 101.5367]}>
                  <Popup>
                    <strong>{t.popupCompany}</strong><br />
                    63-4B, Tingkat 4, Jalan Anggerik Vanilla T 31/T,<br />
                    Kota Kemuning, 40460 Shah Alam, Selangor
                  </Popup>
                </Marker>
              </MapContainer>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
